import React, { useState, useEffect, useRef } from 'react';
import * as api from './api';
import './App.css';

type Phase = 'landing' | 'session';
type SessionStep = 1 | 2 | 3 | 4 | 5;

function App() {
  const [phase, setPhase] = useState<Phase>('landing');
  const [token, setToken] = useState<string>(() => new URLSearchParams(window.location.search).get('token') || sessionStorage.getItem('ss_token') || '');
  const [session, setSession] = useState<any>(null);
  const [step, setStep] = useState<SessionStep>(1);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [regulator, setRegulator] = useState('');
  const [payAck, setPayAck] = useState(false);

  // Session state
  const [documents, setDocuments] = useState<any[]>([]);
  const [allegations, setAllegations] = useState<any[]>([]);
  const [caseStatus, setCaseStatus] = useState({ interimOrder: '', hearingDate: '', stage: '' });
  const [reportProgress, setReportProgress] = useState<any>(null);
  const [stageInfo, setStageInfo] = useState<any>(null);

  // Chat
  const [chatMessages, setChatMessages] = useState<{role:string;text:string}[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const REGS = ['NMC','HCPC','GMC','GDC','GPhC','GOC','GOsC','GCC','SWE','SRA','BSB','Other'];

  // Demo mode — skip payment, show all steps with sample data
  function enterDemo() {
    setSession({ name: 'Demo User', expiry: new Date(Date.now() + 14*86400000).toISOString() });
    setPhase('session');
    setStep(2);
    setDocuments([{name:'Demo-Schedule-of-Allegations.pdf', size:45000}]);
    setAllegations([
      { number: '1', text: 'On or around 15 March 2024, you failed to maintain adequate patient records in relation to Patient A.', position: '', note: '' },
      { number: '2', text: 'On or around 22 March 2024, in relation to Patient B:', position: '', note: '' },
      { number: '2a', text: 'You administered medication without checking their allergy status.', position: '', note: '' },
      { number: '2b', text: 'You failed to record the administration of medication.', position: '', note: '' },
      { number: '2c', text: 'You subsequently altered the patient records to conceal the error.', position: '', note: '' },
      { number: '3', text: 'Your conduct at charge 2c above was dishonest.', position: '', note: '' },
    ]);
    setChatMessages([{role:'assistant', text:'This is a demo session. In a real session, your case documents would be analysed and the allegations would be extracted from them.'}]);
  }

  // Token validation
  useEffect(() => {
    if (token) {
      api.validateToken(token).then(res => {
        if (res.valid) {
          sessionStorage.setItem('ss_token', token);
          setSession(res);
          setPhase('session');
          setChatMessages([{role:'assistant', text:'Welcome back to Sequentus Preview. I\'m here to help you understand your case. You can ask me questions at any time using this chat.'}]);

          // Restore step from Dataverse
          const savedStep = res.currentStep || 1;
          setStep(savedStep as SessionStep);
          if (res.interimOrder) setCaseStatus(cs => ({...cs, interimOrder: res.interimOrder}));
          if (res.hearingDate) setCaseStatus(cs => ({...cs, hearingDate: res.hearingDate}));

          api.getDocuments(token).then(d => {
            if (d.documents?.length) setDocuments(d.documents);
          }).catch(() => {});
        }
      }).catch(() => {});
    }
  }, [token]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({behavior:'smooth'}); }, [chatMessages]);

  // Handlers
  async function handleCheckout() {
    if (!name || !email || !payAck) return;
    setLoading(true);
    try {
      const res = await api.createCheckoutSession(email, name, regulator);
      if (res.url) window.location.href = res.url;
      else setError(res.error || 'Payment failed');
    } catch (e:any) { setError(e.message); }
    setLoading(false);
  }

  async function handleUpload(files: FileList|null) {
    if (!files?.length) return;
    setLoading(true);
    for (const f of Array.from(files)) await api.uploadDocument(token, f).catch(()=>{});
    const docs = await api.getDocuments(token);
    setDocuments(docs.documents || []);
    setLoading(false);
  }

  async function handleExtractAllegations() {
    setLoading(true);
    const res = await api.extractAllegations(token);
    setAllegations((res.allegations||[]).map((a:any) => ({...a, position:'', note:''})));
    setStep(2);
    setLoading(false);
  }

  async function handleConfirmAndGenerate() {
    setLoading(true);
    if (token) {
      await api.confirmAllegations(token, [...allegations, {caseStatus}]);
      await api.generateReport(token);
      const iv = setInterval(async () => {
        const s = await api.getReportStatus(token);
        setReportProgress(s);
        if (s.complete) { clearInterval(iv); setStep(3); setLoading(false); }
      }, 5000);
    } else {
      // Demo mode — skip to step 3 immediately
      setTimeout(() => { setStep(3); setLoading(false); }, 1000);
    }
  }

  async function handleSendChat() {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(p => [...p, {role:'user',text:msg}]);
    const res = await api.sendMessage(token, msg).catch(() => ({reply:'Sorry, something went wrong.'}));
    setChatMessages(p => [...p, {role:'assistant',text:res.reply}]);
  }

  async function handleReflectionUpload(files: FileList|null) {
    if (!files?.length) return;
    setLoading(true);
    const file = files[0];
    try {
      const res = await api.submitReflection(token, file);
      if (res.ok) {
        setChatMessages(p => [...p, {role:'assistant',text:'Your reflection has been reviewed and detailed feedback has been sent to your email. Take your time with the questions — a good reflection develops over several drafts. You can upload a revised version at any time during your access period.'}]);
      } else {
        setChatMessages(p => [...p, {role:'assistant',text:`There was an issue reviewing your reflection: ${res.error || 'Please try again.'}`}]);
      }
    } catch (e: any) {
      setChatMessages(p => [...p, {role:'assistant',text:'Sorry, something went wrong processing your reflection. Please try again.'}]);
    }
    setLoading(false);
  }

  return (
    <div className="ss">
      <header className="ss-header">
        <div className="header-brand"><img src="/logo.png" alt="Sequentus" className="header-logo" /><span className="header-sub">Preview</span></div>
        {phase === 'session' && (
          <button className="chat-toggle" onClick={() => setChatOpen(!chatOpen)}>
            {chatOpen ? 'Close chat' : 'Ask a question'}
          </button>
        )}
      </header>

      <main className="ss-main">
        {error && <div className="ss-error">{error}<button onClick={()=>setError('')}>&times;</button></div>}

        {/* ═══ LANDING ═══ */}
        {phase === 'landing' && (
          <div className="landing">
            <section className="hero">
              <h1>Sequentus Preview</h1>
              <p className="hero-sub">A structured, independent analysis of the legal issues in your regulatory case</p>
              <div className="hero-price">&pound;120 <span>+ VAT</span></div>
            </section>

            <section className="features">
              <div className="feature-card">
                <h3>What you get</h3>
                <ul>
                  <li>Analysis of every allegation in your case</li>
                  <li>Explanation of your regulatory process and what to expect</li>
                  <li>Guidance on writing an effective reflection</li>
                  <li>AI-powered feedback on your draft reflection</li>
                  <li>14 days of access to a case assistant</li>
                </ul>
              </div>
              <div className="feature-card">
                <h3>How it works</h3>
                <ol>
                  <li>Upload your case documents</li>
                  <li>Confirm the allegations and your position</li>
                  <li>Receive a stage-by-stage guide to your process</li>
                  <li>Get help writing your reflection</li>
                  <li>Submit your reflection for AI feedback</li>
                </ol>
              </div>
              <div className="feature-card accent">
                <h3>Fee offset</h3>
                <p>If you instruct Sequentus for full representation, the &pound;120 is deducted from your fee.</p>
              </div>
              <div className="feature-card">
                <h3>Important</h3>
                <p>This is <strong>not legal advice</strong>. Results are generated by AI. This does not replace legal representation.</p>
              </div>
            </section>

            {regulator && <RegulatorStats regulator={regulator} />}

            <LoginBox />

            <section className="landing-form">
              <h2>Get started</h2>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your full name" />
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Your email address" />
              <select value={regulator} onChange={e=>setRegulator(e.target.value)}>
                <option value="">Select your regulator</option>
                {REGS.map(r => <option key={r}>{r}</option>)}
              </select>
              <label className="ack-check"><input type="checkbox" checked={payAck} onChange={e=>setPayAck(e.target.checked)} /><span>I understand that this service does not provide legal advice and that results are generated by AI</span></label>
              <button className="btn-pay" onClick={handleCheckout} disabled={loading||!payAck}>{loading ? 'Processing...' : 'Pay \u00a3120 + VAT and get started'}</button>
              <p className="form-note">Secure payment via Stripe</p>
              <button className="btn-demo" onClick={enterDemo}>View demo</button>
            </section>
          </div>
        )}

        {/* ═══ SESSION ═══ */}
        {phase === 'session' && (
          <div className="session">
            {/* Progress bar */}
            <div className="step-progress">
              {[1,2,3,4,5].map(s => (
                <div key={s} className={`step-dot ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
                  <span>{s}</span>
                  <label>{['Upload','Allegations','Your process','Reflection guide','Feedback'][s-1]}</label>
                </div>
              ))}
            </div>

            {/* Step 1: Upload */}
            {step === 1 && (
              <div className="step-page">
                <h2>Upload your documents</h2>
                <p className="step-desc">Upload the documents you've received from the regulator — schedule of allegations, witness statements, any correspondence about your case.</p>
                <div className="drop-zone" onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();handleUpload(e.dataTransfer.files);}}>
                  <input type="file" id="fu" multiple accept=".pdf,.doc,.docx,.txt" style={{display:'none'}} onChange={e=>handleUpload(e.target.files)} />
                  <label htmlFor="fu">{loading ? 'Uploading...' : 'Drop files here or click to upload'}</label>
                </div>
                {documents.length > 0 && <div className="doc-list">
                  {documents.map((d,i) => <div key={i} className="doc-row">{d.name} <span>{(d.size/1024).toFixed(0)} KB</span></div>)}
                  <button className="btn-next" onClick={handleExtractAllegations} disabled={loading}>{loading ? 'Analysing...' : 'Continue'}</button>
                </div>}
              </div>
            )}

            {/* Step 2: Allegations + Case Status */}
            {step === 2 && (
              <div className="step-page">
                <h2>The allegations in your case</h2>
                <p className="step-desc">We've identified these from your documents. Please confirm your position on each one.</p>

                {allegations.length > 0 ? (
                  <div className="allegation-list">
                    {allegations.map((a,i) => {
                      const isParent = allegations.some((other:any,j:number) => j !== i && other.number && a.number && other.number.startsWith(a.number) && other.number.length > a.number.length);
                      return (
                        <div key={i} className={`a-card ${a.number && /[a-z]$/i.test(a.number) ? 'sub-allegation' : ''}`}>
                          <div className="a-top">
                            {a.editing ? (
                              <input className="a-num-edit" value={a.number||''} onChange={e=>{const u=[...allegations];u[i]={...a,number:e.target.value};setAllegations(u);}} placeholder="#" />
                            ) : (
                              <span className="a-num">{a.number||i+1}</span>
                            )}
                            {a.editing ? (
                              <textarea className="a-edit" value={a.text} onChange={e=>{const u=[...allegations];u[i]={...a,text:e.target.value};setAllegations(u);}} rows={2} placeholder="Describe the allegation..." />
                            ) : (
                              <p>{a.text} <button className="a-edit-btn" onClick={()=>{const u=[...allegations];u[i]={...a,editing:true};setAllegations(u);}}>edit</button></p>
                            )}
                          </div>
                          {!isParent && (
                            <>
                              <div className="a-pos">
                                {['Accept','Deny','Don\'t know'].map(p => (
                                  <button key={p} className={`pos-btn ${a.position===p?'sel':''}`} onClick={()=>{const u=[...allegations];u[i]={...a,position:p,editing:false};setAllegations(u);}}>{p}</button>
                                ))}
                              </div>
                              <input className="a-note" placeholder="Optional note..." value={a.note||''} onChange={e=>{const u=[...allegations];u[i]={...a,note:e.target.value};setAllegations(u);}} />
                            </>
                          )}
                          {isParent && <p className="a-parent-note">Respond to each sub-allegation below</p>}
                          <button className="a-remove" onClick={()=>setAllegations(allegations.filter((_:any,j:number)=>j!==i))}>&times;</button>
                        </div>
                      );
                    })}

                    <div className="a-add-section">
                      <button className="a-add-btn" onClick={()=>{
                        setAllegations([...allegations, {number:'', text:'', position:'', note:'', editing:true}]);
                      }}>+ Add missing allegation</button>
                    </div>

                    <div className="case-status-section">
                      <h3>Your current situation</h3>
                      <div className="status-q">
                        <p>Are you currently subject to any interim restrictions?</p>
                        <div className="a-pos">
                          {['No restrictions','Interim conditions of practice','Interim suspension','Not sure'].map(o => (
                            <button key={o} className={`pos-btn ${caseStatus.interimOrder===o?'sel':''}`} onClick={()=>setCaseStatus({...caseStatus,interimOrder:o})}>{o}</button>
                          ))}
                        </div>
                      </div>
                      <div className="status-q">
                        <p>Do you have a hearing date?</p>
                        <input type="date" value={caseStatus.hearingDate} onChange={e=>setCaseStatus({...caseStatus,hearingDate:e.target.value})} className="date-input" />
                      </div>
                    </div>

                    {allegations.filter((a:any) => !allegations.some((o:any,j:number) => o.number && a.number && o.number.startsWith(a.number) && o.number.length > a.number.length)).every((a:any)=>a.position) && caseStatus.interimOrder && (
                      <button className="btn-next" onClick={handleConfirmAndGenerate} disabled={loading}>
                        {loading ? (reportProgress ? `Generating... ${reportProgress.progress||''}` : 'Processing...') : 'Continue'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="loading-state">Analysing your documents...</div>
                )}
              </div>
            )}

            {/* Step 3: Stage-specific explainer */}
            {step === 3 && (
              <div className="step-page">
                <h2>Your regulatory process</h2>
                <p className="step-desc">Based on your case, here's what to expect at each stage of the process.</p>

                <div className="explainer-grid">
                  <div className="explainer-box done">
                    <div className="ex-icon">&#10003;</div>
                    <h3>Referral &amp; Investigation</h3>
                    <p>Your case has been referred to the regulator and investigated. The facts have been established.</p>
                  </div>

                  {caseStatus.interimOrder && caseStatus.interimOrder !== 'No restrictions' && (
                    <div className="explainer-box current">
                      <div className="ex-icon">!</div>
                      <h3>Interim Order</h3>
                      <p>You are currently subject to {caseStatus.interimOrder.toLowerCase()}. This will be reviewed periodically until the substantive hearing.</p>
                    </div>
                  )}

                  <div className={`explainer-box ${caseStatus.hearingDate ? 'upcoming' : 'future'}`}>
                    <div className="ex-icon">{caseStatus.hearingDate ? '&#9650;' : '&#8226;'}</div>
                    <h3>Substantive Hearing</h3>
                    <p>{caseStatus.hearingDate
                      ? `Your hearing is scheduled for ${new Date(caseStatus.hearingDate).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})}. This is when the panel will consider the allegations and decide on any sanction.`
                      : 'A hearing date has not yet been set. When it is, the panel will consider the allegations and decide on any sanction.'
                    }</p>
                  </div>

                  <div className="explainer-box future">
                    <div className="ex-icon">&#8226;</div>
                    <h3>Panel Decision</h3>
                    <p>The panel will decide whether the allegations are proven, whether your fitness to practise is impaired, and if so, what sanction to impose.</p>
                  </div>

                  <div className="explainer-box future">
                    <div className="ex-icon">&#8226;</div>
                    <h3>After the Hearing</h3>
                    <p>You may be able to appeal the decision. If a suspension or conditions order is imposed, it will be reviewed before it expires.</p>
                  </div>
                </div>

                <button className="btn-next" onClick={()=>setStep(4)}>Continue to reflection guidance</button>
              </div>
            )}

            {/* Step 4: Reflection guidance */}
            {step === 4 && (
              <div className="step-page">
                <h2>Writing your reflection</h2>
                <p className="step-desc">A well-written reflection is one of the most important things you can prepare. It demonstrates to the panel that you understand what went wrong and what you've done about it.</p>

                <div className="guidance-grid">
                  <div className="guidance-card">
                    <h3>What good insight looks like</h3>
                    <ul>
                      <li>Acknowledge specifically what you did wrong — not in general terms</li>
                      <li>Explain the impact on the patient, colleagues, or the profession</li>
                      <li>Show you understand <em>why</em> it was wrong, not just that it was</li>
                      <li>Avoid blame, minimisation, or deflection</li>
                    </ul>
                  </div>

                  <div className="guidance-card">
                    <h3>Structure your reflection</h3>
                    <ol>
                      <li><strong>What happened</strong> — in your own words, briefly</li>
                      <li><strong>What you've learned</strong> — specific insights, not platitudes</li>
                      <li><strong>What you've done</strong> — concrete steps, training, changes</li>
                      <li><strong>What you'd do differently</strong> — show forward thinking</li>
                    </ol>
                  </div>

                  <div className="guidance-card">
                    <h3>Common mistakes</h3>
                    <ul>
                      <li>Writing a defence statement instead of a reflection</li>
                      <li>Being too general — "I've learned my lesson" isn't enough</li>
                      <li>Focusing on the process rather than the conduct</li>
                      <li>Failing to mention the people affected</li>
                    </ul>
                  </div>

                  <div className="guidance-card accent">
                    <h3>How a lawyer can help</h3>
                    <p>A lawyer can help you frame your reflection in a way that addresses the specific concerns the panel will be looking at. Your &pound;120 analysis fee is offset against the instructing fee if you choose to instruct Sequentus.</p>
                    <a href="mailto:enquiries@sequentus.org?subject=Preview%20-%20Representation" className="guide-cta">Contact Sequentus</a>
                  </div>
                </div>

                <p style={{marginTop:24,color:'#94a3b8',fontSize:14}}>When you're ready, write your reflection (aim for around 600 words) and upload it on the next step for AI feedback.</p>
                <button className="btn-next" onClick={()=>setStep(5)}>Continue to upload reflection</button>
              </div>
            )}

            {/* Step 5: Reflection upload + feedback */}
            {step === 5 && (
              <div className="step-page">
                <h2>Upload your reflection</h2>
                <p className="step-desc">Upload your draft reflection (PDF or Word, around 600 words). Our AI will review it and send you detailed feedback by email.</p>

                <div className="drop-zone" onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();handleReflectionUpload(e.dataTransfer.files);}}>
                  <input type="file" id="ru" accept=".pdf,.doc,.docx,.txt" style={{display:'none'}} onChange={e=>handleReflectionUpload(e.target.files)} />
                  <label htmlFor="ru">{loading ? 'Processing...' : 'Drop your reflection here or click to upload'}</label>
                </div>

                <div className="reflection-tips">
                  <h3>Before you submit</h3>
                  <ul>
                    <li>Have you addressed each allegation specifically?</li>
                    <li>Have you explained the impact on others?</li>
                    <li>Have you described what you've done to address the issues?</li>
                    <li>Is it in your own words and voice?</li>
                    <li>Is it around 600 words?</li>
                  </ul>
                </div>

                <div className="next-steps-box">
                  <h3>What happens next</h3>
                  <p>You'll receive detailed feedback on your reflection by email within a few minutes. Your case assistant remains available for 14 days — use it to ask questions, review your analysis, or explore the regulatory process.</p>
                  <p>If you'd like professional legal support, Sequentus can help. Your &pound;120 is offset against the instructing fee.</p>
                  <a href="mailto:enquiries@sequentus.org?subject=Preview%20-%20Representation" className="guide-cta">Contact Sequentus for representation</a>
                </div>
              </div>
            )}

            {/* Chat panel */}
            {chatOpen && (
              <div className="chat-panel">
                <div className="chat-header">
                  <span>Case Assistant</span>
                  <button onClick={()=>setChatOpen(false)}>&times;</button>
                </div>
                <div className="chat-msgs">
                  {chatMessages.map((m,i) => <div key={i} className={`msg ${m.role}`}><div className="bubble">{m.text}</div></div>)}
                  <div ref={chatEndRef} />
                </div>
                <form className="chat-input" onSubmit={e=>{e.preventDefault();handleSendChat();}}>
                  <input value={chatInput} onChange={e=>setChatInput(e.target.value)} placeholder="Ask about your case..." />
                  <button type="submit">Send</button>
                </form>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="ss-footer">Sequentus Preview &copy; {new Date().getFullYear()} &middot; This is not legal advice &middot; Results are generated by AI</footer>
    </div>
  );
}

function RegulatorStats({ regulator }: { regulator: string }) {
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    if (regulator) api.getRegulatorStats(regulator).then(setStats).catch(() => {});
  }, [regulator]);

  if (!stats || !stats.total_hearings) return null;

  return (
    <section className="reg-stats">
      <h3>{regulator} — Last 30 Days</h3>
      <div className="reg-stats-grid">
        <div className="rs-item"><span className="rs-num">{stats.total_hearings}</span><span className="rs-label">Hearings</span></div>
        {stats.struck_off > 0 && <div className="rs-item"><span className="rs-num">{stats.struck_off}</span><span className="rs-label">Struck off</span></div>}
        {stats.suspended > 0 && <div className="rs-item"><span className="rs-num">{stats.suspended}</span><span className="rs-label">Suspended</span></div>}
        {stats.conditions > 0 && <div className="rs-item"><span className="rs-num">{stats.conditions}</span><span className="rs-label">Conditions</span></div>}
      </div>
      <p className="rs-source">Source: Sequentus Intelligence — based on published regulatory data</p>
    </section>
  );
}

function LoginBox() {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginMsg, setLoginMsg] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  async function handleLogin() {
    if (!loginEmail.includes('@') || !acknowledged) return;
    setLoginLoading(true);
    const res = await api.requestLoginLink(loginEmail);
    setLoginMsg(res.message || 'Login link sent. Check your email.');
    setLoginLoading(false);
  }

  return (
    <section className="login-box">
      <h3>Already have an account?</h3>
      <div className="login-row">
        <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Enter your email address" />
        <button onClick={handleLogin} disabled={loginLoading || !acknowledged || !loginEmail.includes('@')}>{loginLoading ? 'Sending...' : 'Send login link'}</button>
      </div>
      <label className="ack-check"><input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} /><span>I understand that this service does not provide legal advice and that results are generated by AI</span></label>
      {loginMsg && <p className="login-msg">{loginMsg}</p>}
    </section>
  );
}

export default App;
